import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CircleAlert, Loader2, Monitor, Smartphone } from 'lucide-react';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import type { AvisoVersion, Idioma, TipoDestinatario, Version, VistaPrevia as Previa } from '@/lib/comunicaciones';

/**
 * El correo tal y como saldrá, generado por el servidor con la misma función
 * que el envío. Se pide medio segundo después de dejar de escribir.
 *
 * Va en un iframe sin scripts (`sandbox` sin `allow-scripts`): el HTML es un
 * correo, no una página, y así nada de lo que se escriba en el editor puede
 * ejecutarse en el CRM. Los enlaces se abren en otra pestaña.
 */
export default function VistaPrevia({ version, idioma, tipo, remitente, onAvisos }: {
  version: Version;
  idioma: Idioma;
  tipo: TipoDestinatario;
  remitente?: string;
  onAvisos?: (a: AvisoVersion[]) => void;
}) {
  const [previa, setPrevia] = useState<Previa | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [movil, setMovil] = useState(false);
  const [alto, setAlto] = useState(600);
  const iframe = useRef<HTMLIFrameElement>(null);
  const avisosRef = useRef(onAvisos);
  avisosRef.current = onAvisos;

  const clave = JSON.stringify({ version, idioma, tipo });
  useEffect(() => {
    let vigente = true;
    setCargando(true);
    const t = setTimeout(() => {
      api.vistaPreviaCampana(version, idioma, tipo)
        .then(p => {
          if (!vigente) return;
          setPrevia(p);
          setError(null);
          avisosRef.current?.(p.avisos);
        })
        .catch(err => { if (vigente) setError(err instanceof Error ? err.message : 'Sin vista previa'); })
        .finally(() => { if (vigente) setCargando(false); });
    }, 500);
    return () => { vigente = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clave]);

  const html = previa ? previa.html.replace('<head>', '<head><base target="_blank">') : '';
  const medir = () => {
    const doc = iframe.current?.contentDocument;
    if (doc?.body) setAlto(Math.max(300, doc.documentElement.scrollHeight));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-sereno-dark/50 flex items-center gap-2">
          Vista previa {cargando && <Loader2 size={12} className="animate-spin" />}
        </div>
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden">
          <button type="button" title="Escritorio" onClick={() => setMovil(false)} className={classNames('px-2 py-1', !movil ? 'bg-sereno-dark text-white' : 'text-sereno-dark/50')}><Monitor size={14} /></button>
          <button type="button" title="Móvil" onClick={() => setMovil(true)} className={classNames('px-2 py-1', movil ? 'bg-sereno-dark text-white' : 'text-sereno-dark/50')}><Smartphone size={14} /></button>
        </div>
      </div>

      {/* Lo que se ve en la bandeja antes de abrirlo */}
      <div className="bg-white border border-gray-200 rounded-xl px-4 py-3">
        <div className="text-xs text-sereno-dark/40">{remitente || 'Sereno'}</div>
        <div className="text-sm font-semibold text-sereno-dark truncate">{previa?.asunto || <span className="text-sereno-dark/30">Sin asunto</span>}</div>
        <div className="text-xs text-sereno-dark/50 truncate">{previa?.preheader || <span className="text-sereno-dark/30">Sin preheader</span>}</div>
      </div>

      {error && <div className="text-xs text-red-600 flex items-center gap-1"><CircleAlert size={14} /> {error}</div>}

      <div className="rounded-xl border border-gray-200 overflow-hidden bg-[#FAF7F0] flex justify-center">
        {previa && (
          <iframe
            ref={iframe}
            title="Vista previa del correo"
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            srcDoc={html}
            onLoad={medir}
            style={{ width: movil ? 375 : '100%', maxWidth: '100%', height: alto, border: 0, display: 'block' }}
          />
        )}
      </div>

      {previa && previa.avisos.length > 0 && (
        <ul className="space-y-1">
          {previa.avisos.map((a, i) => (
            <li key={i} className={classNames('text-xs flex items-start gap-1.5', a.nivel === 'error' ? 'text-red-600' : 'text-amber-700')}>
              {a.nivel === 'error' ? <CircleAlert size={13} className="mt-0.5 shrink-0" /> : <AlertTriangle size={13} className="mt-0.5 shrink-0" />}
              {a.texto}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
